import { Box, Collapse, List, Stack, Tab, Tabs, Typography, useMediaQuery } from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import CalendarFilterField from '../CalendarToolbar/CalendarFilterField';
import AppointmentListItem from 'FRONTEND/components/Calendar/AppointmentList/AppointmentListItem';
import HighlightedAppointment from 'FRONTEND/components/Calendar/HighlightedAppointment';
import Loader from 'FRONTEND/components/Loader';
import {
  cancelRequest as cancelAppointmentRequest,
  listRequest as listAppointmentsRequest,
  updateRequest as updateAppointmentRequest,
} from 'FRONTEND/reducers/appointmentSlice';
import { appointmentListMounted, appointmentListUnmounted, goToActorCalendar } from 'FRONTEND/reducers/calendarSlice';
import { selectExpertEntities } from 'FRONTEND/reducers/expertSlice';
import { selectAppoinmentAndAvailabilityLocations } from 'FRONTEND/reducers/locationSlice';
import { RootState } from 'FRONTEND/reducers/types';
import {
  selectStatusAndTypeFilteredCalendarAppointments,
  selectExpertNextAppointment,
  selectExpertOngoingAppointment,
} from 'FRONTEND/selectors/appointmentSelectors';
import { Expert } from 'FRONTEND/utils/backendApi/expert/expert.types';
import { EXPERT } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';
import { PJAX_MAIN_CONTAINER_SELECTOR } from 'LEGACY/constants';

type AppointmentListProps = {
  rtcServerUrl: string;
  showFilters?: boolean;
  layout?: 'horizontal' | 'vertical'; // Not mergable with displayMode because mobile views override this even in panel mode. Worth keeping the distinction.
  isSquare?: boolean;
  displayMode?: 'panel' | 'default'; // Controls various UI conditions, mainly to distinguish RightPanel (panel) rendering from Calendar (default) rendering.
  filterUnrestricted?: boolean; // Whether to query only appointments with unrestricted=true on the backend. Defaults to unrestrictedMode, but callers can override it (e.g. patient portal appointments are restricted).
};

const AppointmentList = ({
  rtcServerUrl,
  showFilters,
  layout,
  isSquare,
  displayMode = 'default',
  filterUnrestricted = displayMode === 'panel',
}: AppointmentListProps) => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(appointmentListMounted());
    return () => {
      dispatch(appointmentListUnmounted());
    };
  }, []);

  // Filter appointments between these dates for unrestricted mode (msf)
  const startDate = dayjs().subtract(12, 'hour').toISOString();
  const endDate = dayjs().add(12, 'hour').toISOString();
  // Expert managements
  const managedExpertIdList = useSelector((state: RootState) => state.expert.managedIdList);
  useEffect(() => {
    dispatch(listAppointmentsRequest({
      actorId: managedExpertIdList,
      startDate: displayMode === 'panel' ? startDate : null, // Not sure this should be tied to displayMode, worth reconsidering. Perhaps a new prop (with configurable date ranges instead of hardcoded 12h)
      endDate: displayMode === 'panel' ? endDate : null,
      isUnrestricted: filterUnrestricted,
    }));
  }, []);

  // Appointment list management
  const appointmentList = useSelector(selectStatusAndTypeFilteredCalendarAppointments);
  const nextAppointment = useSelector((state: RootState) => selectExpertNextAppointment(state, userId, new Date().toJSON()));
  const ongoingAppointment = useSelector((state: RootState) => selectExpertOngoingAppointment(state, userId, new Date().toJSON()));
  const highlightedAppointmentRef = useRef(null);
  useEffect(() => {
    if (highlightedAppointmentRef.current && (nextAppointment || ongoingAppointment)) {
      highlightedAppointmentRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [nextAppointment, ongoingAppointment]);

  const locations = useSelector(selectAppoinmentAndAvailabilityLocations);
  const checkedLocationsList = useSelector((state: RootState) => state.location.checkedLocationsList);
  const locationFilteredAppointments = locations.length > 0 ? (
    appointmentList.filter((appointment) => checkedLocationsList.includes(appointment?.location?.id) || !appointment?.location)
  ) : appointmentList;

  // Tab state
  const [selectedTab, setSelectedTab] = useState('upcoming');
  const handleTabChange = (event, newTab) => {
    if (newTab !== null) {
      setSelectedTab(newTab);
    }
  };

  // Filter by tab (future/past)
  const filteredAppointments = locationFilteredAppointments.filter((appointment) => {
    if (selectedTab === 'upcoming') {
      return dayjs(appointment.endDate).isAfter(dayjs());
    } else {
      return dayjs(appointment.endDate).isBefore(dayjs());
    }
  });

  // Actions management
  const selectedExpertId = useSelector((state: RootState) => state.booking.selectedExpertId);
  const handleAcceptAppointmentClick = (originalAppointment) => {
    dispatch(updateAppointmentRequest({
      originalAppointment,
      acceptedIdList: [selectedExpertId],
    }));
  };
  const handleDeclineAppointmentClick = (originalAppointment) => {
    dispatch(updateAppointmentRequest({
      originalAppointment,
      declinedIdList: [selectedExpertId],
    }));
  };
  const handleCancelAppointmentClick = (appointmentId) => {
    // TODO: add confirm modal
    dispatch(cancelAppointmentRequest({ appointmentId }));
  };
  const handlePatientRecordClick = (appointment) => {
    $.pjax({ url: `${appointment.patient.reference}history/`, container: PJAX_MAIN_CONTAINER_SELECTOR });
  };
  // We need useCallback to prevent the re-rendering of the avatars which cause blinking
  const handleExpertClick = useCallback((expertId) => {
    dispatch(goToActorCalendar({ actorId: expertId, actorType: EXPERT }));
  }, []);

  const expertMap = useSelector((state: RootState) => selectExpertEntities(state));
  const isLoading = useSelector((state: RootState) => state.app.requestCounter[listAppointmentsRequest.toString()] > 0);
  const isSmall = useMediaQuery('(max-width:800px)');
  const userId = useSelector((state: RootState) => state.user.id);

  return (
    <Stack direction='column' sx={{ minHeight: 0 }}>
      {showFilters &&
        <Stack width='100%' alignItems='end' mt={2}>
          <Box maxWidth={300}>
            <CalendarFilterField />
          </Box>
        </Stack>}
      {displayMode === 'default' && (
        <Stack width='100%' direction='row' justifyContent='center' alignItems='center' mb={2}>
          <Tabs value={selectedTab} onChange={handleTabChange}>
            <Tab label={i18n._('Upcoming')} value='upcoming' sx={{ mx: isSmall ? 2 : 10, justifyContent: 'center' }} />
            <Tab label={i18n._('Past')} value='past' sx={{ mx: isSmall ? 2 : 10, justifyContent: 'center' }} />
          </Tabs>
        </Stack>
      )}
      {filteredAppointments.length > 0 ? (
        <List disablePadding sx={{ py: 0, px: 0.5, gap: 1, overflowY: 'auto' }}>
          <TransitionGroup component={null}>
            {filteredAppointments.map((appointment) => {
              const expert = expertMap[appointment.expertId];

              if (displayMode === 'panel') {
                if (appointment.id === ongoingAppointment?.id || (appointment.id === nextAppointment?.id && !ongoingAppointment)) {
                  return (
                    <Collapse key={`${appointment.id}-${appointment.expertId}`} ref={highlightedAppointmentRef}>
                      <Box sx={{ mb: 1 }}>
                        <HighlightedAppointment rtcServerUrl={rtcServerUrl} displayMode={displayMode} />
                      </Box>
                    </Collapse>
                  );
                }
              }

              const isPastAppointment = appointment.endDate < new Date().toJSON();

              return (
                <Collapse key={`${appointment.id}-${appointment.expertId}`} sx={{ my: 1 }}>
                  <AppointmentListItem
                    expert={expert as Expert}
                    appointment={appointment}
                    layout={layout || isSmall ? 'vertical' : 'horizontal'}
                    onAcceptAppointmentClick={handleAcceptAppointmentClick}
                    onDeclineAppointmentClick={handleDeclineAppointmentClick}
                    onCancelAppointmentClick={handleCancelAppointmentClick}
                    onPatientRecordClick={handlePatientRecordClick}
                    onExpertClick={handleExpertClick}
                    userId={userId}
                    rtcServerUrl={rtcServerUrl}
                    isSquare={isSquare}
                    displayMode={displayMode}
                    isPastAppointment={displayMode === 'panel' && isPastAppointment}
                  />
                </Collapse>
              );
            })}
          </TransitionGroup>
        </List>
      ) : !isLoading && (
        <Box p='15px'>
          <Typography>{selectedTab === 'upcoming' ? i18n._('No upcoming appointment.') : i18n._('No past appointment.')}</Typography>
        </Box>
      )}
      <Loader isLoading={isLoading && !appointmentList.length} />
    </Stack>
  );
};

export default AppointmentList;
